import React, { useCallback, useEffect, useRef, useState } from 'react'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { Editor as TiptapEditor } from '@tiptap/core'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Col, Input, Row } from 'reactstrap'
import './editor.css'

interface EditorProps {
  onChange: (value: string) => void
  defaultValue: string
}

interface ToolbarButtonProps {
  active?: boolean
  children: React.ReactNode
  disabled?: boolean
  onClick: () => void
  title: string
}

function getEditorHtml(editor: TiptapEditor) {
  return editor.isEmpty ? '' : editor.getHTML()
}

function ToolbarButton({
  active = false,
  children,
  disabled = false,
  onClick,
  title,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={`rte-toolbar-button${active ? ' is-active' : ''}`}
      disabled={disabled}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  )
}

export const Editor = ({ onChange, defaultValue }: EditorProps) => {
  const [sourceHtml, setSourceHtml] = useState(defaultValue)
  const [linkUrl, setLinkUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [, setToolbarVersion] = useState(0)
  const previousDefaultValue = useRef(defaultValue)

  const publishHtml = useCallback(
    (editor: TiptapEditor) => {
      const html = getEditorHtml(editor)
      setSourceHtml(html)
      onChange(html)
    },
    [onChange],
  )

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        allowBase64: false,
      }),
    ],
    content: defaultValue,
    editorProps: {
      attributes: {
        class: 'rte-editor-content',
      },
    },
    onUpdate: ({ editor }) => {
      publishHtml(editor)
    },
    onSelectionUpdate: () => {
      setToolbarVersion((version) => version + 1)
    },
  })

  useEffect(() => {
    if (!editor || defaultValue === previousDefaultValue.current) {
      return
    }

    previousDefaultValue.current = defaultValue
    editor.commands.setContent(defaultValue, { emitUpdate: false })
    publishHtml(editor)
  }, [defaultValue, editor, publishHtml])

  const setLink = () => {
    if (!editor) {
      return
    }

    const url = linkUrl.trim()

    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink()
.run()
      publishHtml(editor)
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url })
.run()
    publishHtml(editor)
  }

  const addImage = () => {
    if (!editor) {
      return
    }

    const url = imageUrl.trim()

    if (!url) {
      return
    }

    editor.chain().focus().setImage({ src: url }).run()
    setImageUrl('')
    publishHtml(editor)
  }

  const onChangeSource = (event: React.ChangeEvent<HTMLInputElement>) => {
    const html = event.target.value
    setSourceHtml(html)

    if (!editor) {
      onChange(html)
      return
    }

    editor.commands.setContent(html, { emitUpdate: false })
    onChange(getEditorHtml(editor))
  }

  return (
    <>
      <div className="rte-editor">
        <div className="rte-toolbar" aria-label="Rich text editor toolbar">
          <ToolbarButton
            active={editor?.isActive('bold')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            title="Bold"
          >
            B
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('italic')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            title="Italic"
          >
            I
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('code')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleCode().run()}
            title="Inline code"
          >
            {'</>'}
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('heading', { level: 2 })}
            disabled={!editor}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
            title="Heading medium"
          >
            H2
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('heading', { level: 3 })}
            disabled={!editor}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
            title="Heading small"
          >
            H3
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('bulletList')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            title="Bullet list"
          >
            UL
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('orderedList')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            title="Numbered list"
          >
            OL
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('blockquote')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            title="Blockquote"
          >
            "
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('codeBlock')}
            disabled={!editor}
            onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
            title="Code block"
          >
            Code
          </ToolbarButton>
        </div>

        <Row className="g-2 mb-2">
          <Col md={8}>
            <Input
              type="url"
              className="form-control"
              placeholder="Link URL"
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
            />
          </Col>
          <Col md={4}>
            <button
              type="button"
              className="btn btn-secondary w-100"
              onClick={setLink}
            >
              Apply Link
            </button>
          </Col>
        </Row>

        <Row className="g-2 mb-3">
          <Col md={8}>
            <Input
              type="url"
              className="form-control"
              placeholder="Image URL"
              value={imageUrl}
              onChange={(event) => setImageUrl(event.target.value)}
            />
          </Col>
          <Col md={4}>
            <button
              type="button"
              className="btn btn-secondary w-100"
              onClick={addImage}
            >
              Insert Image
            </button>
          </Col>
        </Row>

        <EditorContent editor={editor} />
      </div>

      <Row className="mt-4">
        <Col>
          <Input
            type="textarea"
            className="form-control"
            id="editor-source"
            name="editor-source"
            rows={25}
            placeholder="Editor Source"
            value={sourceHtml}
            onChange={onChangeSource}
          />
        </Col>
      </Row>
    </>
  )
}
